import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, CheckCircle2, MapPin, Clock, Shield, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import ContactSection from '../ContactSection';
import Seo from '../../seo/Seo';
import Breadcrumbs from './Breadcrumbs';
import FAQAccordion from './FAQAccordion';
import Gallery from './Gallery';
import {
  SITE, buildLocalBusinessJsonLd, buildServiceJsonLd, buildFaqJsonLd, buildBreadcrumbJsonLd,
} from '../../seo/siteMeta';

/**
 * Shared layout for every /locations/* page.
 *
 * A location page hyper-targets the "<service> in <town>" search intent.
 * It is structurally similar to a service page, but:
 *  - The H1 leads with the town name.
 *  - Hero copy emphasises local response time, postcodes covered, local landmarks.
 *  - Internal links push back to the main service page for topical authority.
 *
 * Props:
 *   townName        — e.g. "Rochester"
 *   townSlug        — e.g. "rochester"  (lowercase, used in URL)
 *   serviceKey      — "plumber" | "handyman" (drives copy + meta)
 *   serviceLabel    — human-readable label, e.g. "Plumber"
 *   coreServiceSlug — slug of the main service page to link back to, e.g. "plumbing"
 *   postcodes       — array of postcode prefixes/areas covered, e.g. ["ME1","ME2","ME3"]
 *   localLandmarks  — optional array of local-area reference points
 *   intro           — optional override of the hero intro paragraph
 *   children        — body content (long-form, unique per town)
 *   faqs            — array of {q, a}
 *   galleryImages   — array of gallery image objects
 */
const LocationPageLayout = ({
  townName,
  townSlug,
  serviceKey, // 'plumber' | 'handyman'
  serviceLabel, // 'Plumber' | 'Handyman'
  coreServiceSlug, // 'plumbing' | 'handyman'
  postcodes = [],
  localLandmarks = [],
  intro,
  children,
  faqs = [],
  galleryImages = [],
  region = 'Kent',
  seoTitle,
  seoDescription,
}) => {
  const canonical = `/locations/${serviceKey}-${townSlug}`;
  const pageTitle = `${serviceLabel} in ${townName} — H.A.R.P Solutions LTD`;
  const finalSeoTitle = seoTitle || `${serviceLabel} in ${townName}, ${region} | H.A.R.P Solutions LTD`;
  const finalSeoDesc = seoDescription || `Trusted local ${serviceLabel.toLowerCase()} in ${townName}, ${region}. Fast response across ${postcodes.join(', ')}. Call +44 330 122 1094 — fully insured engineers.`;

  const breadcrumbItems = [
    { label: 'Home', url: '/' },
    { label: 'Locations', url: '/#featured-services' },
    { label: `${serviceLabel} in ${townName}`, url: canonical },
  ];

  const jsonLd = [
    buildLocalBusinessJsonLd(),
    buildServiceJsonLd({
      name: `${serviceLabel} services in ${townName}`,
      slug: `${serviceKey}-${townSlug}`,
      description: finalSeoDesc,
      areaServed: [`${townName}, ${region}`],
    }),
    buildFaqJsonLd(faqs),
    buildBreadcrumbJsonLd(breadcrumbItems),
  ];

  return (
    <>
      <Seo
        title={finalSeoTitle}
        description={finalSeoDesc}
        canonical={canonical}
        keywords={`${serviceLabel} ${townName}, ${townName} ${serviceLabel}, ${serviceLabel.toLowerCase()} near me, ${serviceLabel.toLowerCase()} ${region}, ${townName} plumber, ${townName} handyman`}
        jsonLd={jsonLd}
      />
      <Header />

      <main className="bg-gray-950 text-white pt-24" data-testid={`location-page-${serviceKey}-${townSlug}`}>
        <section className="border-b border-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <Breadcrumbs items={breadcrumbItems} />
            <div className="mt-6 grid lg:grid-cols-5 gap-10 items-start">
              <div className="lg:col-span-3">
                <h1
                  className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight text-white"
                  data-testid="location-h1"
                >
                  {pageTitle}
                </h1>
                <p className="mt-4 text-lg text-amber-500 font-medium">
                  Local {serviceLabel.toLowerCase()} serving {townName}, {region} and surrounding areas.
                </p>
                {intro ? (
                  <p className="mt-5 text-gray-300 text-base sm:text-lg leading-relaxed max-w-2xl">{intro}</p>
                ) : (
                  <p className="mt-5 text-gray-300 text-base sm:text-lg leading-relaxed max-w-2xl">
                    H.A.R.P Solutions provides reliable {serviceLabel.toLowerCase()} services across {townName} — from emergency callouts
                    to scheduled repairs and property maintenance. Our engineers know the area, the local property stock, and the typical
                    issues that come up in {region} homes.
                  </p>
                )}

                {postcodes.length > 0 && (
                  <div className="mt-6">
                    <p className="text-gray-400 text-sm uppercase tracking-wide mb-2">Postcodes covered</p>
                    <div className="flex flex-wrap gap-2">
                      {postcodes.map((pc) => (
                        <span key={pc} className="px-3 py-1 rounded border border-gray-700 bg-gray-900/60 text-sm text-gray-200">
                          {pc}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href={`tel:${SITE.phoneTel}`}
                    className="inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-md font-semibold transition-colors"
                    data-testid="location-call-cta"
                  >
                    <Phone className="w-4 h-4" /> Call {SITE.phone}
                  </a>
                  <a
                    href="#location-contact"
                    className="inline-flex items-center justify-center gap-2 border border-gray-700 hover:border-amber-500/60 text-white px-6 py-3 rounded-md font-medium transition-colors"
                    data-testid="location-quote-cta"
                  >
                    Request a Quote <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>

              <aside className="lg:col-span-2 rounded-xl border border-gray-800 bg-gray-900/60 p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="text-white font-semibold">Fast Local Response</p>
                    <p className="text-gray-400 text-sm">Engineers based across {region} — often onsite same-day.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="text-white font-semibold">Fully Insured</p>
                    <p className="text-gray-400 text-sm">Public-liability insured engineers, every job documented.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="text-white font-semibold">{townName} &amp; surrounding</p>
                    <p className="text-gray-400 text-sm">{localLandmarks.slice(0, 4).join(', ') || `${townName} and nearby areas`}.</p>
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-800">
                  <Link to={`/services/${coreServiceSlug}`} className="inline-flex items-center gap-1 text-amber-500 hover:underline text-sm">
                    <Wrench className="w-4 h-4" /> See full {coreServiceSlug.replace('-', ' ')} services <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="border-b border-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 prose prose-invert prose-amber max-w-none">
            {children}
          </div>
        </section>

        <section className="border-b border-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <Gallery serviceSlug={`${serviceKey}-${townSlug}`} images={galleryImages} title={`Recent ${serviceLabel} Jobs in ${townName}`} />
          </div>
        </section>

        {faqs.length > 0 && (
          <section className="border-b border-gray-900">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">Frequently Asked Questions</h2>
              <p className="text-gray-400 mb-8">Common questions from {townName} customers.</p>
              <FAQAccordion faqs={faqs} />
            </div>
          </section>
        )}

        <section className="border-b border-gray-900 bg-gray-900/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">Related Services</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { slug: 'plumbing', label: 'Plumbing' },
                { slug: 'emergency-plumber', label: 'Emergency Plumber' },
                { slug: 'handyman', label: 'Handyman' },
                { slug: 'property-maintenance', label: 'Property Maintenance' },
              ].map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="group block rounded-lg border border-gray-800 hover:border-amber-500/50 bg-gray-900/40 p-5 transition-colors"
                  data-testid={`related-service-${s.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-white font-semibold group-hover:text-amber-500">{s.label}</span>
                    <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-amber-500" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <div id="location-contact"><ContactSection /></div>
      </main>

      <Footer />
    </>
  );
};

export default LocationPageLayout;
